import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { patchUserClientSettings, UserPreferences, UserSettings } from '../../shared/api/api';
import { AppDispatch, GetState } from '../../shared/services/store/types';
import { logErrorService } from '../../shared/services';
import { customPreferencesSelector } from './CustomPreferencesSelectors';

const CURRENT_VERSION = 1;

export interface BaseCustomPreferencesStore {
  fetching: boolean;
  version: number;
  preferences: UserPreferences;
}

const initialState: BaseCustomPreferencesStore = {
  fetching: false,
  version: CURRENT_VERSION,
  preferences: {},
};

export const CustomPreferencesSlice = createSlice({
  name: 'CustomPreferences',
  initialState,
  reducers: {
    setPreferences(state, action: PayloadAction<Omit<BaseCustomPreferencesStore, 'fetching'>>) {
      state.fetching = false;
      state.version = action.payload.version;
      state.preferences = action.payload.preferences;
    },
    startFetching(state) {
      state.fetching = true;
    },
    stopFetching(state) {
      state.fetching = false;
    },
  },
  extraReducers: builder => {
    builder.addCase('AUTHENTICATION_LOGOUT', () => initialState);
  },
});

export const CustomPreferencesActions = CustomPreferencesSlice.actions;

// Migrate a stored preferences blob from an older schema version to the current one. No migration
// steps are defined yet; when the `UserPreferences` shape changes, transform older payloads here
// based on `fromVersion` before they reach the store.
const migratePreferences = (preferences: UserPreferences, fromVersion: number): UserPreferences => {
  if (fromVersion < CURRENT_VERSION) {
    // Future migration steps go here.
  }
  return preferences;
};

// Sync hydrate from a user payload that already contains `settings` (e.g. the `getUserInformation`
// response on login). Avoids an extra `users/self` fetch when we already have the data.
export const hydrateCustomPreferencesFromUser = (settings: UserSettings | undefined) => (dispatch: AppDispatch) => {
  const client = settings?.client;
  const version = client?.version ?? CURRENT_VERSION;
  const preferences = migratePreferences(client?.preferences ?? {}, version);
  dispatch(
    CustomPreferencesActions.setPreferences({
      version: CURRENT_VERSION,
      preferences,
    })
  );
};

interface UpdatePreferencesOptions {
  // Apply the merged preferences to the store synchronously before the PATCH resolves, so selectors
  // re-read the new value immediately. Reverts to the pre-update snapshot if the PATCH fails.
  optimistic?: boolean;
}

export const updateCustomPreferencesState =
  (newPreferences: UserPreferences, options: UpdatePreferencesOptions = {}) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const { version: currentVersion, preferences: currentPreferences } = customPreferencesSelector(getState());
    const mergedPreferences: UserPreferences = { ...currentPreferences, ...newPreferences };

    if (options.optimistic) {
      dispatch(CustomPreferencesActions.setPreferences({ version: currentVersion, preferences: mergedPreferences }));
    }
    dispatch(CustomPreferencesActions.startFetching());

    try {
      const responseClient = await patchUserClientSettings({ version: CURRENT_VERSION, preferences: mergedPreferences });
      dispatch(
        CustomPreferencesActions.setPreferences({
          version: responseClient?.version ?? CURRENT_VERSION,
          preferences: responseClient?.preferences ?? mergedPreferences,
        })
      );
    } catch (error) {
      logErrorService('Failed to update user preferences', error);
      if (options.optimistic) {
        dispatch(CustomPreferencesActions.setPreferences({ version: currentVersion, preferences: currentPreferences }));
      } else {
        dispatch(CustomPreferencesActions.stopFetching());
      }
    }
  };

export const customPreferencesReducer = CustomPreferencesSlice.reducer;
